import { reflectKeys } from "@utils/key.reflactor.ts";

export const MO_FETCH_INTERVAL = [1000, 1000, 1000, 1000, 1000, 2000, 2000, 2000, 5000, 5000];
export const MO_DEFAULT_FETCH_INTERVAL = 10000;

export const MEDIA_TYPE = reflectKeys([
  "AUDIO",
  "HTML5",
  "IMAGE",
  "VIDEO",
  "EXCEL",
  "POWERPOINT",
  "WORD",
  "PDF",
  "ZIP",
  "OTHERS",
  "YOUTUBE",
]);

export type MediaType = keyof typeof MEDIA_TYPE;

export const FILE_MEDIA_TYPE = [
  "AUDIO",
  "HTML5",
  "IMAGE",
  "VIDEO",
  "EXCEL",
  "POWERPOINT",
  "WORD",
  "PDF",
  "ZIP",
  "OTHERS",
];

export const MEDIA_FILE_STATUS = reflectKeys([
  "READY", // File ready to view/download
  "UPLOADING", // Uploading File (Default/Initial status)
  "SCANNING", // Virus Scanning
  "PROCESSING", // Transcoding Video
  "TRANSCODING_ERROR", // Video Transcoding Error
  "ERROR", // System Error
  "PROTECTED", // Password Protected File
  "VIRUS", // Virus Detected
  "SIZE_EXCEEDED_ERROR", // File Size Exceeded
]);

export const MEDIA_RECORD_TYPE = reflectKeys(["MO", "UGC"]);

export type MediaFileStatus = keyof typeof MEDIA_FILE_STATUS;

export const TRANSCRIPTION_STATUS = reflectKeys(["IN_PROGRESS", "COMPLETED", "FAILED"]);

export const CHARACTER_LIMIT = {
  TITLE: 100,
  DESCRIPTION: 500,
  KEYWORDS: 250,
  TRANSCRIPT: 250,
};

export const MEDIA_OBJECT_TYPE = {
  AUDIO: { value: "AUDIO", displayName: "Audio", iconName: "Audio32" },
  HTML5: { value: "HTML5", displayName: "HTML5", iconName: "Interactive32" },
  IMAGE: { value: "IMAGE", displayName: "Image", iconName: "Image32" },
  VIDEO: { value: "VIDEO", displayName: "Video", iconName: "Video32" },
  EXCEL: { value: "EXCEL", displayName: "Excel", iconName: "Excel32" },
  POWERPOINT: { value: "POWERPOINT", displayName: "PowerPoint", iconName: "Powerpoint32" },
  WORD: { value: "WORD", displayName: "Word", iconName: "Word32" },
  PDF: { value: "PDF", displayName: "PDF", iconName: "PDF32" },
  ZIP: { value: "ZIP", displayName: "Zip", iconName: "Folder32" },
  OTHERS: { value: "OTHERS", displayName: "Others", iconName: "Folder32" },
};

export const RESOURCE_STATUS = {
  DRAFT: { value: "DRAFT", label: "media_object_resource_status_draft" },
  PENDING_APPROVAL: {
    value: "PENDING_APPROVAL",
    label: "media_object_resource_status_pending_approval",
  },
  APPROVED: { value: "APPROVED", label: "media_object_resource_status_approved" },
  REJECTED: { value: "REJECTED", label: "media_object_resource_status_rejected" },
  UNPUBLISHED: { value: "UNPUBLISHED", label: "media_object_resource_status_unpublished" },
};

export const MEDIA_OBJECT_COPYRIGHT_OWNER = reflectKeys([
  "MOE_OWNED",
  "MOE_OWNED_WITH_LICENSED_MATERIAL",
  "MOE_CO_OWNED",
  "MOE_CO_OWNED_WITH_LICENSED_MATERIAL",
  "LICENSED_MATERIALS",
  "TEACHER_UPLOADED",
]);

export const HTML5_ACTIVITY_ID_PREFIX = "http://adlnet.gov/expapi/activities/question/";

export const MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION = {
  MOE_OWNED: { value: "MOE_OWNED", label: "media_object_copyright_ownership_moe_owned", name: "0" },
  MOE_OWNED_WITH_LICENSED_MATERIAL: {
    value: "MOE_OWNED_WITH_LICENSED_MATERIAL",
    label: "media_object_copyright_ownership_moe_owned_licensed_material",
    name: "1",
  },
  MOE_CO_OWNED: {
    value: "MOE_CO_OWNED",
    label: "media_object_copyright_ownership_moe_co_owned",
    name: "2",
  },
  MOE_CO_OWNED_WITH_LICENSED_MATERIAL: {
    value: "MOE_CO_OWNED_WITH_LICENSED_MATERIAL",
    label: "media_object_copyright_ownership_moe_co_owned_licensed_material",
    name: "3",
  },
  LICENSED_MATERIALS: {
    value: "LICENSED_MATERIALS",
    label: "media_object_copyright_ownership_licensed_material",
    name: "4",
  },
  OTHERS: { value: "OTHERS", label: "media_object_option_others", name: "5" },
};

export const MOE_COPYRIGHT_AUTHOR = "MOE";

export const RMS_TYPE = {
  IMAGE_ILLUSTRATION: {
    value: "IMAGE_ILLUSTRATION",
    label: "media_object_resource_type_image_illustration",
    name: "0",
  },
  AUDIO_MUSIC: { value: "AUDIO_MUSIC", label: "media_object_resource_type_audio_music", name: "1" },
  PUBLICATION: { value: "PUBLICATION", label: "media_object_resource_type_publication", name: "2" },
  INTERACTIVE: {
    value: "INTERACTIVE",
    label: "media_object_resource_type_interactives",
    name: "3",
  },
  IDT: { value: "IDT", label: "media_object_resource_type_idt", name: "4" },
  TEXT_DATA: { value: "TEXT_DATA", label: "media_object_resource_type_text_data", name: "5" },
  TEXT_DATA_WITH_IMAGE: {
    value: "TEXT_DATA_WITH_IMAGE",
    label: "media_object_resource_type_text_data_with_image",
    name: "6",
  },
  VIDEO_ANIMATION: {
    value: "VIDEO_ANIMATION",
    label: "media_object_resource_type_video_animation",
    name: "7",
  },
  OTHERS: { value: "OTHERS", label: "media_object_option_others", name: "8" },
};

export const LICENSE_TYPE = {
  CURRENT_PRINT_PUBLICATION: {
    value: "CURRENT_PRINT_PUBLICATION",
    label: "media_object_licence_type_for_current_print_publication",
    name: "0",
  },
  TERM: { value: "TERM", label: "media_object_licence_type_term_licence", name: "1" },
  PERPETUAL: {
    value: "PERPETUAL",
    label: "media_object_licence_type_perpetual_licence",
    name: "2",
  },
  CLEARED_BY_LSB: {
    value: "CLEARED_BY_LSB",
    label: "media_object_licence_type_cleared_by_lsb_for_use",
    name: "3",
  },
  PUBLIC_DOMAIN: {
    value: "PUBLIC_DOMAIN",
    label: "media_object_licence_type_public_domain",
    name: "4",
  },
  COPYRIGHT_EXCEPTION_INTERNET_USE: {
    value: "COPYRIGHT_EXCEPTION_INTERNET_USE",
    label: "media_object_licence_type_copyright_exception_internet_use",
    name: "5",
  },
  COPYRIGHT_EXCEPTION_SMALL_PORTION: {
    value: "COPYRIGHT_EXCEPTION_SMALL_PORTION",
    label: "media_object_licence_type_copyright_exception_copying_small_portion_of_work",
    name: "6",
  },
  COPYRIGHT_EXCEPTION_EDUCATIONAL: {
    value: "COPYRIGHT_EXCEPTION_EDUCATIONAL",
    label:
      "media_object_licence_type_copyright_exception_copying_authorial_works_for_educational_purposes",
    name: "7",
  },
  COPYRIGHT_EXCEPTION_FAIR_USE: {
    value: "COPYRIGHT_EXCEPTION_FAIR_USE",
    label: "media_object_licence_type_copyright_exception_fair_use",
    name: "8",
  },
  COPYRIGHT_EXCEPTION_OTHERS: {
    value: "COPYRIGHT_EXCEPTION_OTHERS",
    label: "media_object_licence_type_copyright_exception_others_for_users_to_input",
    name: "9",
  },
};

export const MEDIA_OBJECT_COPYRIGHT_TERRITORY = {
  SG: { value: "SG", label: "media_object_licence_territory_sg", name: "0" },
  SGHK: { value: "SGHK", label: "media_object_licence_territory_sg_hk", name: "1" },
  WORLDWIDE: { value: "WORLDWIDE", label: "media_object_licence_territory_worldwide", name: "2" },
};

export const SINGLE_USE = {
  YES: { value: "YES", label: "yes_label", name: "0" },
  NO: { value: "NO", label: "no_label", name: "1" },
};

export const REPRODUCTION = {
  OWNER_GROUP: {
    value: "OWNER_GROUP",
    label: "media_object_right_for_owner_group_only",
    name: "0",
  },
  OWNER_GROUP_TEACHER: {
    value: "OWNER_GROUP_TEACHER",
    label: "media_object_right_for_owner_group_and_teachers",
    name: "1",
  },
  OWNER_GROUP_TEACHER_OTHERS: {
    value: "OWNER_GROUP_TEACHER_OTHERS",
    label: "media_object_right_for_owner_group_teachers_and_other_end_users",
    name: "2",
  },
  YES: { value: "YES", label: "media_object_right_no_restrictions", name: "3" },
  NO: { value: "NO", label: "media_object_right_not_reproducible", name: "4" },
  NA: { value: "NA", label: "media_object_right_na", name: "5" },
};

export const DOWNLOADABLE_RIGHT = {
  OWNER_GROUP: {
    value: "OWNER_GROUP",
    label: "media_object_right_for_owner_group_only",
    description: "media_object_downloadable_right_for_owner_group_only_description",
    name: "0",
  },
  OWNER_GROUP_TEACHER: {
    value: "OWNER_GROUP_TEACHER",
    label: "media_object_right_for_owner_group_and_teachers",
    description: "media_object_downloadable_right_for_owner_group_and_teachers_description",
    name: "1",
  },
  OWNER_GROUP_TEACHER_OTHERS: {
    value: "OWNER_GROUP_TEACHER_OTHERS",
    label: "media_object_right_for_owner_group_teachers_and_other_end_users",
    description:
      "media_object_downloadable_right_for_owner_group_teachers_and_other_end_users_description",
    name: "2",
  },
  YES: {
    value: "YES",
    label: "media_object_right_no_restrictions",
    description: "media_object_downloadable_right_downloadable_description",
    name: "3",
  },
  NO: {
    value: "NO",
    label: "media_object_right_not_downloadable",
    description: "media_object_downloadable_right_not_downloadable_description",
    name: "4",
  },
  NA: {
    value: "NA",
    label: "media_object_right_na",
    description: "media_object_downloadable_right_not_downloadable_description",
    name: "5",
  },
};

export const PRINTABLE_RIGHT = {
  OWNER_GROUP: {
    value: "OWNER_GROUP",
    label: "media_object_right_for_owner_group_only",
    description: "media_object_printable_right_for_owner_group_only_description",
    name: "0",
  },
  OWNER_GROUP_TEACHER: {
    value: "OWNER_GROUP_TEACHER",
    label: "media_object_right_for_owner_group_and_teachers",
    description: "media_object_printable_right_for_owner_group_and_teachers_description",
    name: "1",
  },
  OWNER_GROUP_TEACHER_OTHERS: {
    value: "OWNER_GROUP_TEACHER_OTHERS",
    label: "media_object_right_for_owner_group_teachers_and_other_end_users",
    description:
      "media_object_printable_right_for_owner_group_teachers_and_other_end_users_description",
    name: "2",
  },
  YES: {
    value: "YES",
    label: "media_object_right_no_restrictions",
    description: "media_object_printable_right_printable_description",
    name: "3",
  },
  NO: {
    value: "NO",
    label: "media_object_right_not_printable",
    description: "media_object_printable_right_not_printable_description",
    name: "4",
  },
  NA: {
    value: "NA",
    label: "media_object_right_na",
    description: "media_object_printable_right_not_printable_description",
    name: "5",
  },
};

export const MODIFICATION_RIGHT = {
  OWNER_GROUP: {
    value: "OWNER_GROUP",
    label: "media_object_right_for_owner_group_only",
    name: "0",
  },
  OWNER_GROUP_TEACHER: {
    value: "OWNER_GROUP_TEACHER",
    label: "media_object_right_for_owner_group_and_teachers",
    name: "1",
  },
  OWNER_GROUP_TEACHER_OTHERS: {
    value: "OWNER_GROUP_TEACHER_OTHERS",
    label: "media_object_right_for_owner_group_teachers_and_other_end_users",
    name: "2",
  },
  YES: { value: "YES", label: "media_object_right_no_restrictions", name: "3" },
  NO: { value: "NO", label: "media_object_right_not_modifiable", name: "4" },
  NA: { value: "NA", label: "media_object_right_na", name: "5" },
};

export const MODIFICATION_TYPE = {
  ADAPT_EDIT: { value: "ADAPT_EDIT", label: "media_object_modification_adapt_edit", name: "0" },
  CUT_OUT_SEGMENTS: {
    value: "CUT_OUT_SEGMENTS",
    label: "media_object_modification_cut_out_segments",
    name: "1",
  },
  USE_PART: { value: "USE_PART", label: "media_object_modification_use_part", name: "2" },
  ADD_TEXT_IMAGE: {
    value: "ADD_TEXT_IMAGE",
    label: "media_object_modification_add_text_image_to_resource",
    name: "3",
  },
  RESIZE: { value: "RESIZE", label: "media_object_modification_resize", name: "4" },
  CROP: { value: "CROP", label: "media_object_modification_crop", name: "5" },
  OTHERS: { value: "OTHERS", label: "media_object_option_others", name: "6" },
  NA: { value: "NA", label: "media_object_right_na", name: "7" },
};

export const AVAILABILITY = {
  ALL_OPAL: { value: "ALL_OPAL", label: "media_object_end_users_access_all_opal", name: "0" },
  TEACHERS: { value: "TEACHERS", label: "media_object_end_users_access_teacher", name: "1" },
  STUDENTS: { value: "STUDENTS", label: "media_object_end_users_access_students", name: "2" },
  ALL_SLS_USERS: {
    value: "ALL_SLS_USERS",
    label: "media_object_end_users_access_all_sls_users",
    name: "3",
  },
  UNRESTRICTED: {
    value: "UNRESTRICTED",
    label: "media_object_end_users_access_unrestricted",
    name: "4",
  },
  RESTRICTED: { value: "RESTRICTED", label: "media_object_end_users_access_restricted", name: "5" },
  LEVEL_AND_SUBJECT: {
    value: "LEVEL_AND_SUBJECT",
    label: "media_object_end_users_access_level_and_subject",
    name: "6",
  },
};

export const ALL_OPTION = { value: "ALL", label: "media_object_select_all_filter_label" };

export const IMAGE_ORIENTATION = reflectKeys(["SQUARE", "LANDSCAPE", "PORTRAIT"]);

export const PDF_PAGE_SELECTION_MEDIA_TYPE = reflectKeys([
  "MO",
  "UGC",
  "RESOURCE",
  "GOOGLE_DOCUMENT",
  "ATTEMPT_GOOGLE_DOCUMENT",
]);

export const IMAGE_DIMENSION = reflectKeys(["SD", "MD", "HD"]);

export type Dimension = keyof typeof IMAGE_DIMENSION;

export const HTML_TOKEN_STORAGE_KEY = "html_interactive_token";
export const HTML_TOKEN_LAST_FETCH_STORAGE_KEY = "html_interactive_token_last_fetch_time";

export const MAX_BULK_EDIT_IP_COUNT = 20;

export const MIN_AUDIO_DURATION_MINUTE = 1; // minutes
export const MAX_DEFAULT_AUDIO_DURATION_MINUTE = 10;
export const MAX_SE_AUDIO_DURATION_MINUTE = 5;
